/**
 * Shared date/time helpers for the admin dashboard.
 *
 * Strategy: store UTC everywhere (MongoDB already does this).
 *
 *  Viewer-local helpers  — use the browser's real timezone automatically via native
 *    Date local getters and `new Date(localStr).toISOString()`.  No hardcoded offset.
 *    Use for: ad scheduling, broadcast scheduling, coupon validity, audit-log filters.
 *
 *  Business helpers      — fix the timezone to Asia/Qatar (+03:00, no DST) so that
 *    settlement boundaries (payout dates, payroll periods) are identical regardless of
 *    where the admin is located.  A payout period must mean the same instant worldwide.
 */

// ── Viewer-local helpers (browser timezone) ────────────────────────────────────

const pad = (n) => String(n).padStart(2, '0');

/**
 * Convert a stored UTC ISO to a `datetime-local` input value (YYYY-MM-DDTHH:mm)
 * in the browser's own timezone.  Works anywhere in the world.
 */
export function utcToLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return (
    d.getFullYear() + '-' +
    pad(d.getMonth() + 1) + '-' +
    pad(d.getDate()) + 'T' +
    pad(d.getHours()) + ':' +
    pad(d.getMinutes())
  );
}

/**
 * Convert a `datetime-local` input value to UTC ISO.
 * The browser interprets the string in its own timezone — no offset constant needed.
 * Returns null when value is empty or invalid.
 */
export function localInputToUTC(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/**
 * Convert a stored UTC ISO to a `type="date"` input value (YYYY-MM-DD)
 * in the browser's own timezone.
 */
export function utcToDateInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
}

/**
 * Convert a `type="date"` input value (YYYY-MM-DD) to a UTC ISO instant at the
 * viewer's local day boundary.
 *   boundary 'start' → 00:00:00.000 local
 *   boundary 'end'   → 23:59:59.999 local
 * Returns null when value is empty or invalid.
 */
export function dateInputToUTC(value, boundary) {
  if (!value) return null;
  const suffix = boundary === 'end' ? 'T23:59:59.999' : 'T00:00:00.000';
  const d = new Date(value + suffix);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

// ── Business (settlement) helpers — fixed Asia/Qatar timezone, +03:00, no DST ─

const BUSINESS_TZ = 'Asia/Qatar';
const BUSINESS_OFFSET = '+03:00';

/**
 * Convert a stored UTC ISO to a `type="date"` input value (YYYY-MM-DD) in the
 * Qatar business timezone.  Use when prefilling settlement date fields so the admin
 * always sees "Qatar calendar day" regardless of their browser timezone.
 */
export function utcToBusinessDateInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // 'en-CA' formats as YYYY-MM-DD natively — no manual splitting needed
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: BUSINESS_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

/**
 * Convert a `type="date"` input value (YYYY-MM-DD) to a UTC ISO instant anchored
 * in the Qatar business timezone.  Settlement boundaries are always in Qatar time
 * so they don't drift based on the admin's location.
 *   boundary 'start' → 00:00:00.000 Qatar (+03:00)
 *   boundary 'end'   → 23:59:59.999 Qatar (+03:00)
 * Returns null when value is empty or invalid.
 */
export function dateInputToBusinessUTC(value, boundary) {
  if (!value) return null;
  const suffix = boundary === 'end' ? 'T23:59:59.999' : 'T00:00:00.000';
  const d = new Date(value + suffix + BUSINESS_OFFSET);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/**
 * Today's date in the Qatar business timezone as YYYY-MM-DD.
 * Use as the `max` attribute on settlement date inputs.
 */
export function todayBusinessIso() {
  return utcToBusinessDateInput(new Date().toISOString());
}
